import { useState, useRef, useEffect } from 'react'
import { ArrowLeft, XCircle } from '@phosphor-icons/react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'

import api from '../../api/api'

import { useOfflineMode } from './hooks/useOfflineMode'

const SLOW_CONNECTION_TIMEOUT = 5000 // 5 seconds

const DeliveryCodeEntry = ({ onBack, onSubmit }) => {
  const { t } = useTranslation(['package-room', 'common'])
  const [deliveryCode, setDeliveryCode] = useState('')
  const [errorMessage, setErrorMessage] = useState('')
  const [isVerifying, setIsVerifying] = useState(false)

  const { isConnectionIssue, cachedSettings, setHasBadConnection } = useOfflineMode()

  const requestTimeoutRef = useRef(null)

  useEffect(() => {
    return () => {
      if (requestTimeoutRef.current) {
        clearTimeout(requestTimeoutRef.current)
      }
    }
  }, [])

  const handleCodeChange = (e) => {
    setDeliveryCode(e.target.value)
    setErrorMessage('')
  }

  const verifyCodeOffline = (code) => {
    if (!cachedSettings || !cachedSettings.carriers) {
      return null
    }

    const carrier = cachedSettings.carriers.find((c) => c.code.toString() === code.toString())
    return carrier || null
  }

  const handleCodeSubmit = async () => {
    if (!deliveryCode.trim()) {
      setErrorMessage(t('package-room:deliveryCodeEntry.errorEmptyCode'))
      return
    }

    const code = deliveryCode.trim()

    // If there's a connection issue, verify against cached settings
    if (isConnectionIssue) {
      const carrier = verifyCodeOffline(code)
      if (carrier) {
        onSubmit(carrier.name)
      } else {
        setErrorMessage(t('package-room:deliveryCodeEntry.errorInvalidCode'))
      }
      return
    }

    try {
      setIsVerifying(true)
      setErrorMessage('')

      // Detect slow uploads and turn on semi-offline mode
      requestTimeoutRef.current = setTimeout(() => {
        setHasBadConnection(true)
        setIsVerifying(false)

        // Try to verify against cached settings due to timeout
        const carrier = verifyCodeOffline(code)
        if (carrier) {
          onSubmit(carrier)
        } else {
          setErrorMessage(t('package-room:deliveryCodeEntry.errorConnectionFailed'))
        }
      }, SLOW_CONNECTION_TIMEOUT)

      const { data } = await api.verifyDeliveryAccessCode(code)
      clearTimeout(requestTimeoutRef.current)
      setIsVerifying(false)
      onSubmit(data.carrier)
    } catch {
      clearTimeout(requestTimeoutRef.current)

      // If we have cached settings, try to verify offline
      const carrier = verifyCodeOffline(code)
      if (carrier) {
        onSubmit(carrier.name)
      } else {
        setErrorMessage(t('package-room:deliveryCodeEntry.errorInvalidCode'))
      }
      setIsVerifying(false)
    }
  }

  return (
    <div className="package-room__content package-room__content--full">
      <button className="package-room__back" onClick={onBack}>
        <ArrowLeft size={24} />
        {t('common:actions.back')}
      </button>
      <h2 className="package-room__title">{t('package-room:deliveryCodeEntry.title')}</h2>
      <div className="package-room__custom-input-container">
        <input
          type="number"
          value={deliveryCode}
          onChange={handleCodeChange}
          placeholder={t('package-room:deliveryCodeEntry.placeholder')}
          className="package-room__custom-input"
          autoFocus
        />
        {errorMessage && (
          <div className="package-room__error-message">
            <XCircle size={20} weight="fill" />
            {errorMessage}
          </div>
        )}
        <button
          className="package-room__submit-button"
          onClick={handleCodeSubmit}
          disabled={!deliveryCode.trim() || isVerifying}
        >
          {isVerifying
            ? t('package-room:deliveryCodeEntry.verifying')
            : t('package-room:deliveryCodeEntry.continue')}
        </button>
      </div>
    </div>
  )
}

DeliveryCodeEntry.propTypes = {
  onBack: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
}

export default DeliveryCodeEntry
